import { TWidget } from 'widgets/Widget';
import { RefElement } from 'widgets/toolbox/RefElement';

import { postJsonData } from 'widgets/toolbox/ajax';

type TRecommendationWidget = {
    title: string;
    subtitle: string;
    widget: string;
    alias: string;
    position: string;
    enableColorSelection: boolean;
    recs: Array<object>;
};
type TEventData = {
    feature: string;
    reco_item_id: string;
    position: string | undefined;
    type: string;
    item_id?: string;
};
declare global {
    interface Window {
        optimizelyWidgets?: {
            recommendations?: Array<TRecommendationWidget>;
            widgetRenderingUrl?: string;
        };
        Peerius?: {
            smartRecsClick: (id: string) => void;
            sendAjax: (url: string) => void;
        };
    }
}

/**
 * @param Widget Base widget for extending
 * @returns Optimizely widget class
 */

function OptimizelyWidgetClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory int_custom_optimizely
     * @class OptimizelyWidget
     * @augments Widget
     * @classdesc Instantiates an Optimizely Carousel widget class
     */
    class OptimizelyWidget extends Widget {
        prefs() {
            return {
                container: '',
                enableTracking: '',
                enableColorSelection: false,
                isDynamicInclude: '',
                position: '',
                feature: '',
                contextProductId: '',
                url: '',
                title: '',
                subtitle: '',
                type: '',
                impressionTrackingUrl: '',
                ...super.prefs()
            };
        }

        init() {
            super.init();

            if (this.prefs().isDynamicInclude) {
                this.triggerDynamicallyRenderedWidget();

                return;
            }

            document.addEventListener('optimizely:renderWidget', (event: Event) => {
                const customEvent = event as CustomEvent;

                this.renderWidget(customEvent?.detail);
            });

            // Cart update re-renders html dynamically. We have to listen to that to render the carousel again.
            this.eventBus().on('cart.updated', 'triggerDynamicallyRenderedWidget');

            this.eventBus().on('optimizely:trackClick', 'trackClick');
        }

        /**
         * Pushes an event to the data layer for tracking purposes.
         *
         * @param eventName - The name of the event to be pushed.
         * @param data - The data associated with the event.
         */
        pushToDataLayer(eventName: string, data: Record<string, unknown>) {
            const dataLayerEvent = {
                event: 'ga4_event',
                event_name: eventName
            };

            dataLayerEvent[eventName] = data;

            window.dataLayer?.push(dataLayerEvent);
        }

        /**
         * Handles the click event on a recommendation carousel product element.
         *
         * Extracts the `itemId` from the provided recommendation product element and, if available,
         * triggers the Peerius smart recommendations click tracking.
         *
         * @param recommendationCard - A single card from the carousel of recommendation products.
         */
        trackClick(recommendationCard: RefElement) {
            const prefs = this.prefs();
            const recommendationObject = recommendationCard?.get(0);
            const itemId = recommendationObject?.dataset?.itemId;
            const productId = recommendationObject?.dataset?.productId;
            const itemPosition = recommendationObject?.dataset?.itemPosition;

            if (!itemId) {
                return;
            }

            const eventData: TEventData = {
                feature: prefs.feature || 'recommendation_optimizely',
                reco_item_id: productId || '',
                position: itemPosition,
                type: prefs.position
            };

            if (prefs.contextProductId) {
                eventData.item_id = prefs.contextProductId;
            }

            this.pushToDataLayer('click_recommendation', eventData);

            if (prefs.enableTracking && window.Peerius && window.Peerius.smartRecsClick) {
                window.Peerius?.smartRecsClick(itemId);
            }
        }

        /**
         * Triggers the rendering of the widget if recommendations are available.
         *
         * This method checks for the presence of stored recommendations in the global
         * `window.optimizelyWidgets.recommendations` object. If recommendations exist,
         * it calls `renderWidget` with the stored recommendations to render the widget.
         */
        triggerDynamicallyRenderedWidget() {
            const storedRecommendations = window.optimizelyWidgets?.recommendations;

            if (!storedRecommendations) {
                return;
            }

            this.renderWidget(storedRecommendations);
        }

        /**
         * Sends a POST request to fetch the rendered carousel HTML for the given recommendations.
         *
         * @param widgetData - Object containing recommendation data and products array.
         * @returns A promise resolving to the response of the POST request.
         */
        fetchRenderedWidget(widgetData: TRecommendationWidget) {
            const requestData = {
                widgetType: this.prefs().type,
                recs: JSON.stringify(widgetData)
            };

            return postJsonData(this.prefs().url || '', requestData);
        }

        /**
         * Sends an impression tracking request containing carousel product ids.
         *
         * @param widgetData - Object containing recommendation data and products array.
         */
        sendAjaxImpressionTracking(widgetData) {
            const recommendationIds = String(widgetData?.recs?.map(item => item.id));
            const impressionTrackingUrl = this.prefs().impressionTrackingUrl;

            if (recommendationIds.length && impressionTrackingUrl.length) {
                window.Peerius?.sendAjax(impressionTrackingUrl.concat(recommendationIds));
            }
        }

        /**
         * Renders the Optimizely carousel widget based on the provided event data.
         *
         * Get the widget data corresponding to the position,
         * update its title and subtitle from preferences and material and price from API product, fetch the rendered carousel HTML,
         * and append it to DOM.
         *
         * @param recommendations - The custom event's recommendations, containing widget data in its `detail` property.
         * @returns A promise that resolves when the carousel has been rendered and appended.
         */
        async renderWidget(recommendations: Array<TRecommendationWidget>) {
            const position = this.prefs().position;
            const widgetData = recommendations?.find(widget => widget.position === position);

            if (!widgetData) {
                return;
            }

            widgetData.title = this.prefs().title;
            widgetData.subtitle = this.prefs().subtitle;
            widgetData.enableColorSelection = this.prefs().enableColorSelection;

            const response = await this.fetchRenderedWidget(widgetData);
            const renderedWidget = response.renderedWidget;

            if (response.success && renderedWidget && renderedWidget.length) {
                this.ref('self')?.append(renderedWidget);
                this.sendAjaxImpressionTracking(widgetData);
            }
        }
    }

    return OptimizelyWidget;
}

export type TOptimizelyWidget = ReturnType<typeof OptimizelyWidgetClassCreator>;

export type TOptimizelyWidgetInstance = InstanceType<TOptimizelyWidget>;

export default OptimizelyWidgetClassCreator;
